import { useAuth0 } from '@auth0/auth0-react'
import { agentGatewayErrorResponseSchema, ORGANIZATION_ID_HEADER } from '@salescore/agent-contract'
import { useSalescoreAuth } from '@salescore/client-base'
import { Data, Effect } from 'effect'
import { useCallback } from 'react'
import type { z } from 'zod'

import { parseJsonText } from '../domain/parseJsonText'

// エージェント用 API（/api/agent/gateway）の呼び出し。playground の useAgentApi から移した

type AgentGatewayErrorResponse = z.infer<typeof agentGatewayErrorResponseSchema>

/**
 * エージェント用 API を呼べなかったときの失敗。
 * unauthenticated：トークンを取れなかった。network：応答がなかった。httpStatus：2xx 以外が返った。
 * invalidResponse：応答の形がスキーマに合わなかった。streamBroken：SSE の本文が途中で切れた。
 * body は、2xx 以外の本文が BE の失敗の形（{ code, message }）に合えばその値。message は利用者向けの文言（AGENT_GATEWAY_ERRORS）
 */
export class AgentApiError extends Data.TaggedError('AgentApiError')<{
  reason: 'unauthenticated' | 'network' | 'httpStatus' | 'invalidResponse' | 'streamBroken'
  status: number | null
  body: AgentGatewayErrorResponse | null
  cause: unknown
}> {}

export type AgentApi = (path: string, init: RequestInit) => Effect.Effect<Response, AgentApiError>

// エージェント用 API の origin。SALESCORE には REST 用の環境変数がないので、SAML のログインと同じく GRAPHQL_URL から導く
const agentApiOrigin = (): string => (process.env.GRAPHQL_URL ?? '').replace(/\/graphql$/v, '')

const failOnErrorStatus = (response: Response): Effect.Effect<Response, AgentApiError> =>
  response.ok
    ? Effect.succeed(response)
    : Effect.promise(async () => await response.text().catch(() => '')).pipe(
        Effect.flatMap((text) =>
          Effect.fail(
            new AgentApiError({
              reason: 'httpStatus',
              status: response.status,
              body: parseJsonText(text, agentGatewayErrorResponseSchema),
              cause: null,
            }),
          ),
        ),
      )

/**
 * 応答の JSON をスキーマで確かめて返す。形が違えば invalidResponse で失敗する。
 * 検証の失敗には応答の中身を載せない（トークンなどを含みうるため。ルートの AGENTS.md の「バリデーション」）
 */
export const parseJsonResponse = <T>(response: Response, schema: z.ZodType<T>): Effect.Effect<T, AgentApiError> =>
  Effect.tryPromise({
    try: async (): Promise<unknown> => await response.json(),
    catch: (cause) => new AgentApiError({ reason: 'invalidResponse', status: response.status, body: null, cause }),
  }).pipe(
    Effect.flatMap((json) => {
      const parsed = schema.safeParse(json)
      return parsed.success
        ? Effect.succeed(parsed.data)
        : Effect.fail(
            new AgentApiError({
              reason: 'invalidResponse',
              status: response.status,
              body: null,
              cause: parsed.error.issues.map(({ path, code }) => ({ path, code })),
            }),
          )
    }),
  )

/**
 * エージェント用 API を呼ぶ関数を返す。トークンを Authorization に、組織 ID を X-Organization-Id に付ける。2xx でなければ失敗にする。
 */
export const useAgentApi = (organizationId: string): AgentApi => {
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0()
  const { token: salescoreToken, isSignedInBySalescoreAuth } = useSalescoreAuth()

  return useCallback(
    (path: string, init: RequestInit) =>
      Effect.gen(function* () {
        // SALESCORE の画面と同じく、SALESCORE 独自の JWT があればそれを、なければ Auth0 の ID トークンを送る。
        // GraphQL はページを読み込んだときのトークンを使い続けるが、エージェントは画面を開いたまま長く使われるので、送るたびに取り直す。
        // getIdTokenClaims は手元の値を返すだけなので、先に getAccessTokenSilently で期限切れのトークンを更新させる（AuthenticatedApolloProvider と同じ順）
        const idToken =
          isSignedInBySalescoreAuth && salescoreToken !== null
            ? salescoreToken
            : yield* Effect.tryPromise({
                try: async () => {
                  await getAccessTokenSilently()
                  return (await getIdTokenClaims())?.__raw
                },
                catch: (cause) => new AgentApiError({ reason: 'unauthenticated', status: null, body: null, cause }),
              })
        if (idToken === undefined) {
          return yield* Effect.fail(
            new AgentApiError({ reason: 'unauthenticated', status: null, body: null, cause: null }),
          )
        }

        const headers = new Headers(init.headers)
        headers.set('authorization', `Bearer ${idToken}`)
        headers.set(ORGANIZATION_ID_HEADER, organizationId)
        if (init.body !== undefined && !headers.has('content-type')) {
          headers.set('content-type', 'application/json')
        }

        const response = yield* Effect.tryPromise({
          try: async (signal) => await fetch(`${agentApiOrigin()}${path}`, { ...init, headers, signal }),
          catch: (cause) => new AgentApiError({ reason: 'network', status: null, body: null, cause }),
        })
        return yield* failOnErrorStatus(response)
      }),
    [getAccessTokenSilently, getIdTokenClaims, isSignedInBySalescoreAuth, organizationId, salescoreToken],
  )
}
